import { useIntl } from 'react-intl';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import { getCampaignOtaWithoutEvent } from './eventsOta.helpers';

import type { DoohReportEvent } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

type Props = {
  event: DoohReportEvent;
  eventName: string;
  /** Campaign OTA, the plan figure the event moves. */
  campaignOta: number | null;
};

const BANNER_DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

/**
 * Banner next to the events card title: the event that added the most target people to the
 * campaign, in absolute terms, and how it moved the campaign OTA.
 */
export default function HighestOtaBanner({ event, eventName, campaignOta }: Readonly<Props>) {
  const { formatMessage, formatDate } = useIntl();
  const { formatCount, formatOta, notAvailable } = useDoohReportFormatters();

  const formatBannerDate = (date: string | null): string | null => {
    const parsed = date ? new Date(date.replace(' ', 'T')) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? formatDate(parsed, BANNER_DATE_OPTIONS) : null;
  };
  const dates = [formatBannerDate(event.startDate), formatBannerDate(event.endDate)].filter(Boolean);
  const campaignOtaWithoutEvent = getCampaignOtaWithoutEvent(campaignOta, event.onTargetUpliftPct);

  return (
    <span className="contextEvents__banner">
      <i className="fa-solid fa-sparkles contextEvents__bannerIcon" />
      <span>
        <strong>
          {formatMessage(
            {
              defaultMessage: '{event} had the highest impact on your target audience:',
              id: 'doohReport.contextEvents.banner.title'
            },
            { event: eventName }
          )}
        </strong>{' '}
        {formatMessage(
          {
            defaultMessage: '+{people} target people added to the campaign ({dates})',
            id: 'doohReport.contextEvents.banner.targetPeople'
          },
          { dates: dates.join(' – ') || notAvailable, people: formatCount(event.reachAddedPoints) }
        )}
        {campaignOtaWithoutEvent !== null &&
          campaignOta !== null &&
          ` · ${formatMessage(
            { defaultMessage: 'campaign OTA {without} → {with}', id: 'doohReport.contextEvents.banner.campaignOta' },
            { with: formatOta(campaignOta), without: formatOta(campaignOtaWithoutEvent) }
          )}`}
      </span>
    </span>
  );
}
